/**
 * Utility pour la validation et la synchronisation stricte Backend ↔ Frontend
 * pour les formulaires Client et Dossier.
 */

/**
 * Normalise les chaînes de caractères (trim)
 */
export const normalizeString = (val) => (val === null || val === undefined ? '' : String(val).trim());

/**
 * Validations pour le formulaire Client (Ajout & Modification)
 * @param {Object} data - Données du formulaire client
 * @returns {Object} fieldErrors - Objet contenant les messages d'erreur indexés par nom de champ
 */
export const validateClientForm = (data) => {
  const errors = {};

  const typeClient = normalizeString(data.typeClient) || 'BT';
  const nom = normalizeString(data.nom);
  const prenom = normalizeString(data.prenom);
  const typeIdentifiant = normalizeString(data.typeIdentifiant) || 'cin';
  const cin = normalizeString(data.cin);
  const passeport = normalizeString(data.passeport);
  const telephone = normalizeString(data.telephone);
  const email = normalizeString(data.email);

  // 1. Nom (Obligatoire)
  if (!nom) {
    errors.nom = 'Le nom du client est obligatoire.';
  }

  // 2. Prénom (Obligatoire uniquement pour BT)
  if (typeClient === 'BT' && !prenom) {
    errors.prenom = 'Le prénom est obligatoire pour un client de type BT.';
  }

  // 3. Identité (CIN XOR Passeport ; Optionnel pour MT)
  if (typeClient !== 'MT') {
    const hasCin = typeIdentifiant === 'cin' && cin !== '';
    const hasPasseport = typeIdentifiant === 'passeport' && passeport !== '';

    if (!hasCin && !hasPasseport) {
      errors[typeIdentifiant] = typeIdentifiant === 'cin' 
        ? 'Veuillez renseigner un CIN.' 
        : 'Veuillez renseigner un numéro de passeport.';
    } else if (hasCin && !/^\d{8}$/.test(cin)) {
      errors.cin = 'Le CIN doit contenir exactement 8 chiffres.';
    } else if (hasPasseport && (passeport.length < 3 || passeport.length > 30)) {
      errors.passeport = 'Le numéro de passeport doit contenir entre 3 et 30 caractères.';
    }
  } else {
    // Si MT et que l'utilisateur renseigne quand même un champ identifiant :
    if (typeIdentifiant === 'cin' && cin !== '' && !/^\d{8}$/.test(cin)) {
      errors.cin = 'Le CIN doit contenir exactement 8 chiffres.';
    } else if (typeIdentifiant === 'passeport' && passeport !== '' && (passeport.length < 3 || passeport.length > 30)) {
      errors.passeport = 'Le numéro de passeport doit contenir entre 3 et 30 caractères.';
    }
  }

  // 4. Téléphone (Optionnel, mais si renseigné = exactement 8 chiffres)
  if (telephone !== '' && !/^\d{8}$/.test(telephone)) {
    errors.telephone = 'Le numéro de téléphone doit contenir exactement 8 chiffres.';
  }

  // 5. Email (Optionnel, mais si renseigné = format email valide)
  if (email !== '' && !/^\S+@\S+\.\S+$/.test(email)) {
    errors.email = 'Email non valide';
  }

  const isValid = Object.keys(errors).length === 0;
  const errorSummary = Object.values(errors).join('\n');

  return { isValid, errors, errorSummary };
};

/**
 * Validations pour le formulaire Dossier (Ajout & Modification)
 * @param {Object} data - Données du dossier
 * @param {Object|String} options - Options ou typeClient ('BT', 'MT', 'BT/MT')
 * @returns {Object} { isValid, errors, errorSummary }
 */
export const validateDossierForm = (data, options = 'BT') => {
  const errors = {};

  const typeClient = typeof options === 'string' ? options : (options?.typeClient || 'BT');
  const requireFinancial = typeof options === 'object' ? !!options?.requireFinancial : false;

  const pompage = !!data.pompage;
  const refSteg = normalizeString(data.referenceSteg);
  const puissance = data.puissance !== '' && data.puissance !== null && data.puissance !== undefined ? Number(data.puissance) : null;
  const montantTotalPaiement = data.montantTotalPaiement !== '' && data.montantTotalPaiement !== null && data.montantTotalPaiement !== undefined ? Number(data.montantTotalPaiement) : null;

  // 1. Référence STEG (Obligatoire si !pompage)
  if (!pompage) {
    if (!refSteg) {
      errors.referenceSteg = 'La référence STEG est obligatoire.';
    } else {
      if (typeClient === 'BT' && !/^\d{9}$/.test(refSteg)) {
        errors.referenceSteg = 'La référence STEG pour un client BT doit comporter exactement 9 chiffres.';
      } else if (typeClient === 'MT' && !/^\d{6}$/.test(refSteg)) {
        errors.referenceSteg = 'La référence STEG pour un client MT doit comporter exactement 6 chiffres.';
      } else if (typeClient === 'BT/MT' && !/^\d{6}$/.test(refSteg) && !/^\d{9}$/.test(refSteg)) {
        errors.referenceSteg = 'La référence STEG doit comporter 6 chiffres (MT) ou 9 chiffres (BT).';
      }
    }
  }

  // 2. Puissance
  if (puissance === null || isNaN(puissance) || puissance <= 0) {
    errors.puissance = 'La puissance (kWc) est obligatoire et doit être supérieure à 0.';
  }

  // 3. Montant Total Paiement & Crédit STEG
  if (montantTotalPaiement !== null && (isNaN(montantTotalPaiement) || montantTotalPaiement < 0)) {
    errors.montantTotalPaiement = 'Le montant total ne peut pas être négatif.';
  }

  if (data.montantSteg !== undefined && data.montantSteg !== null && data.montantSteg !== '') {
    const stegNum = Number(data.montantSteg);
    if (!isNaN(stegNum) && stegNum > 0 && montantTotalPaiement !== null && stegNum > montantTotalPaiement) {
      errors.montantSteg = 'Le crédit STEG ne peut pas dépasser le montant total du dossier.';
    }
  }

  // 4. Validation Acompte (si et seulement si acompteVerse est coché)
  if (data.acompteVerse) {
    const montantComptant = data.montantComptant !== '' && data.montantComptant !== null && data.montantComptant !== undefined ? Number(data.montantComptant) : null;
    if (montantComptant === null || isNaN(montantComptant) || montantComptant <= 0) {
      errors.montantComptant = "Le montant de l'acompte est obligatoire et doit être strictement supérieur à 0 DT.";
    } else if (montantTotalPaiement !== null && montantComptant > montantTotalPaiement) {
      errors.montantComptant = "Le montant de l'acompte ne peut pas dépasser le montant total du dossier.";
    }

    if (!normalizeString(data.datePaiementComptant)) {
      errors.datePaiementComptant = "La date de l'acompte est obligatoire.";
    }

    if (!normalizeString(data.modePaiementComptant)) {
      errors.modePaiementComptant = "Le mode de paiement de l'acompte est obligatoire.";
    }
  }

  const isValid = Object.keys(errors).length === 0;
  const errorSummary = Object.values(errors).join('\n');

  return { isValid, errors, errorSummary };
};

/**
 * Extrait le message d'erreur texte renvoyé par l'API backend
 * @param {Object} err - Erreur Axios
 * @returns {String} Message d'erreur formaté
 */
export const extractBackendError = (err) => {
  const status = err.response?.status;
  const data = err.response?.data;

  if (status === 401) return 'Session expirée. Veuillez vous reconnecter.';
  if (status === 403) return 'Vous n\'avez pas la permission d\'effectuer cette action.';
  if (status === 404) return 'Ressource introuvable. Elle a peut-être été supprimée entre-temps.';

  if (data?.errors) {
    const fieldErrors = Object.values(data.errors).map(e => e.message || String(e));
    return fieldErrors.join(' | ');
  }

  if (data?.message) return data.message;
  if (data?.error) return data.error;

  if (status >= 500) return 'Erreur serveur. Veuillez réessayer dans quelques instants.';
  return err.message || 'Une erreur inattendue est survenue.';
};

/**
 * Associe une erreur HTTP / Backend au champ de formulaire correspondant
 * @param {Object} err - Erreur Axios
 * @returns {Object} { [fieldName]: String }
 */
export const mapBackendErrorToField = (err) => {
  const message = extractBackendError(err);
  const lowMsg = message.toLowerCase();

  let fieldName = null;

  if (lowMsg.includes('cin')) {
    fieldName = 'cin';
  } else if (lowMsg.includes('passeport') || lowMsg.includes('passport')) {
    fieldName = 'passeport';
  } else if (lowMsg.includes('steg') || lowMsg.includes('référence')) {
    fieldName = 'referenceSteg';
  } else if (lowMsg.includes('téléphone') || lowMsg.includes('telephone')) {
    fieldName = 'telephone';
  } else if (lowMsg.includes('email') || lowMsg.includes('mail')) {
    fieldName = 'email';
  } else if (lowMsg.includes('nom du client') || lowMsg.includes('nom est obligatoire')) {
    fieldName = 'nom';
  } else if (lowMsg.includes('prénom') || lowMsg.includes('prenom')) {
    fieldName = 'prenom';
  } else if (lowMsg.includes('puissance')) {
    fieldName = 'puissance';
  } else if (lowMsg.includes('montant')) {
    fieldName = 'montantTotalPaiement';
  }

  if (fieldName) {
    return { [fieldName]: message };
  }
  return {};
};
